<script setup lang="ts">
import { groupBy } from "lodash-es";
import { computed } from "vue";

import type { DictsysInflectionSchemeHeaderElement } from "@/types";

import LeInflectionSchemeHighlight from "./LeInflectionSchemeHighlight.vue";

const props = defineProps<{
  headerElements: DictsysInflectionSchemeHeaderElement[];
  language: string;
}>();

const screenReaderText = computed<string>(() => {
  const tensedHeaderElements = groupBy(props.headerElements, "tense");
  const texts: string[] = [];

  for (const tense in tensedHeaderElements) {
    const tensedTexts = tensedHeaderElements[tense].map(
      (element) => (element.prefixes ? element.prefixes.join("/") + " " : "") + element.texts.join("/")
    );

    texts.push(`${tense}: ${tensedTexts.join(", ")}`);
  }

  return texts.join(". ");
});

// A list of header elements may have the same tense, but it should only be shown for the last element.
const shouldDisplayTense = (elementIndex: number): boolean => {
  const element = props.headerElements[elementIndex];

  if (!element.tense) {
    return false;
  }

  if (elementIndex === props.headerElements.length - 1) {
    return true;
  }

  const nextElement = props.headerElements[elementIndex + 1];
  return element.tense !== nextElement.tense;
};
</script>
<template>
  <div v-if="props.headerElements.length > 0" class="le-inflection-scheme-header">
    <span v-for="(element, elementIndex) in props.headerElements" :key="elementIndex" aria-hidden="true">
      <span v-if="element.prefixes" class="le-inflection-scheme-header__text le-inflection-scheme-header__spacing">
        <template v-for="(prefix, prefixIndex) in element.prefixes" :key="prefixIndex"
          ><span :class="{ 'le-inflection-scheme-header__text--bold': element.type === 'inflection' }">{{
            prefix
          }}</span
          ><template v-if="prefixIndex < element.prefixes.length - 1">/</template></template
        >
      </span>

      <span
        v-if="element.type === 'headWord'"
        class="le-inflection-scheme-header__text le-inflection-scheme-header__text--bold"
        :class="{
          'le-inflection-scheme-header__spacing': shouldDisplayTense(elementIndex)
        }"
        >{{ element.texts.join("/") }}</span
      ><span
        v-else
        class="le-inflection-scheme-header__text"
        :class="{ 'le-inflection-scheme-header__spacing': shouldDisplayTense(elementIndex) }"
      >
        <template v-for="(text, textIndex) in element.texts" :key="textIndex">
          <LeInflectionSchemeHighlight
            :text="text"
            :language="props.language"
            class="le-inflection-scheme-header__text--bold"
          /><span v-if="textIndex < element.texts.length - 1">/</span>
        </template> </span
      ><span v-if="shouldDisplayTense(elementIndex)" class="le-inflection-scheme-header__text"
        >({{ element.tense }})</span
      ><span
        v-if="elementIndex < props.headerElements.length - 1"
        class="le-inflection-scheme-header__text le-inflection-scheme-header__spacing"
        >,
      </span>
    </span>
    <div class="sr-only">{{ screenReaderText }}</div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-inflection-scheme-header {
  display: flex;
  flex-wrap: wrap;
  color: @colors[@neutrals][500];

  &__text {
    display: inline-block;
    .dictsys-inflection-scheme-text--large();

    &--bold {
      .dictsys-article-text--bold();
    }
  }

  &__spacing {
    margin-right: 5px;
  }
}
</style>
